export enum RelativeTimeUnit {
  seconds = 'seconds',
  minutes = 'minutes',
  hours = 'hours',
  days = 'days',
  weeks = 'weeks',
  months = 'months',
}

export type RelativeTimeResult = {
  value: number;
  unit: RelativeTimeUnit;
};

export function getRelativeTimeFori18n(time: Date): RelativeTimeResult | null {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - time.getTime()) / 1000);
  const absDiff = Math.abs(diffInSeconds);

  if (absDiff < 60) {
    return { value: absDiff, unit: RelativeTimeUnit.seconds };
  } else if (absDiff < 3600) {
    return { value: Math.floor(absDiff / 60), unit: RelativeTimeUnit.minutes };
  } else if (absDiff < 86400) {
    return { value: Math.floor(absDiff / 3600), unit: RelativeTimeUnit.hours };
  } else if (absDiff < 604800) {
    return { value: Math.floor(absDiff / 86400), unit: RelativeTimeUnit.days };
  } else if (absDiff < 2592000) {
    return { value: Math.floor(absDiff / 604800), unit: RelativeTimeUnit.weeks };
  } else if (absDiff < 31536000) {
    return { value: Math.floor(absDiff / 2592000), unit: RelativeTimeUnit.months };
  }
  return null;
}

export function formatDateToDdMmYyyy(date: Date) {
  return new Intl.DateTimeFormat('en-US', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

export function formatDateToBasedOnLanguage(date: Date, language: string) {
  return new Intl.DateTimeFormat(language, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

export function formatDateToDDMonthYear(date: Date) {
  return new Intl.DateTimeFormat('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

/** Local midnight for the given calendar day (discards time-of-day). */
export function normalizeToLocalMidnight(input: Date): Date {
  return new Date(input.getFullYear(), input.getMonth(), input.getDate());
}

/**
 * Parses `Date`, ISO strings, timestamps, etc. Returns `Date` or `null`.
 * Any other `unknown` input yields `null`. Prefer ISO `YYYY-MM-DD` for strings.
 * When `shouldMidnight` is `true` (default), normalizes to local calendar midnight.
 */
export function parseDateValue(value: unknown, shouldMidnight?: boolean): Date | null {
  const doMidnight = shouldMidnight ?? true;

  if (value == null) {
    return null;
  }

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      return null;
    }

    if (doMidnight) {
      return normalizeToLocalMidnight(value);
    }
    return new Date(value.getTime());
  }

  if (typeof value === 'number') {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) {
      return null;
    }

    if (doMidnight) {
      return normalizeToLocalMidnight(d);
    }
    return d;
  }

  if (typeof value === 'string') {
    const t = Date.parse(value);
    if (Number.isNaN(t)) {
      return null;
    }

    const d = new Date(t);
    if (doMidnight) {
      return normalizeToLocalMidnight(d);
    }
    return d;
  }

  return null;
}

export function getMonthName(date: Date, language: string = 'en-US'): string {
  return new Intl.DateTimeFormat(language, { month: 'long' }).format(date);
}

export function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  return remaining === 0 ? `${hours}h` : `${hours}h ${remaining}m`;
}

export function getThreeMonthsFromDate(
  date: Date,
  language: string = 'en-US'
): [string, string, string] {
  return [0, 1, 2].map(function (offset) {
    const d = new Date(date.getFullYear(), date.getMonth() + offset, 1);
    return getMonthName(d, language);
  }) as [string, string, string];
}
